import React, { useEffect, useState } from 'react';
import { Dumbbell, CalendarDays, MessageCircle } from 'lucide-react';
import { useMemberPortal } from './MemberPortal';
import { getMyPtAssignments, getMyPtLogs } from '../../lib/memberDb';
import { getPtSessionsForMember } from '../../lib/ptSessions';
import { format, parseISO, isAfter } from 'date-fns';
import { ko } from 'date-fns/locale';

export default function MyPT() {
  const { memberRecord } = useMemberPortal();
  const [assignments, setAssignments] = useState([]);
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getMyPtAssignments(memberRecord.id),
      getMyPtLogs(memberRecord.id),
      getPtSessionsForMember(),
    ]).then(([a, l, s]) => {
      setAssignments(a);
      setLogs(l);
      setSessions(s);
    }).catch(e => {
      console.error('PT 데이터 로드 실패:', e);
    }).finally(() => {
      setLoading(false);
    });
  }, [memberRecord.id]);

  const upcomingSessions = sessions.filter(
    s => s.status === 'scheduled' && isAfter(parseISO(s.scheduledAt), new Date())
  );
  const completedSessions = sessions.filter(s => s.status === 'completed');

  if (loading) return <div className="p-4 text-center text-gray-400 text-sm pt-12">불러오는 중...</div>;

  const active = assignments.filter(a => a.status === 'active');
  const completed = assignments.filter(a => a.status === 'completed');

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white">PT 일정</h2>

      {/* 예정 일정 */}
      {upcomingSessions.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <CalendarDays className="w-4 h-4 text-primary-500" />
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">예정 일정</p>
          </div>
          <div className="space-y-2">
            {upcomingSessions.map(s => (
              <div key={s.id} className="card p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{s.productName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">트레이너: {s.trainerName}</p>
                  {s.notes && <p className="text-xs text-gray-400 mt-0.5 italic">"{s.notes}"</p>}
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-primary-600 dark:text-primary-400">
                    {s.scheduledAt ? format(parseISO(s.scheduledAt), 'M월 d일 (EEE)', { locale: ko }) : '-'}
                  </p>
                  <p className="text-xs text-gray-500">{s.scheduledAt ? format(parseISO(s.scheduledAt), 'HH:mm') : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 진행중 PT */}
      {active.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">진행중</p>
          {active.map(a => {
            const pct = a.totalSessions > 0 ? Math.round((a.usedSessions / a.totalSessions) * 100) : 0;
            return (
              <div key={a.id} className="card p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{a.productName}</p>
                    {a.trainerName && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">트레이너: {a.trainerName}</p>
                    )}
                  </div>
                  <span className="text-xs bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 px-2 py-0.5 rounded-full font-medium">
                    {a.usedSessions}/{a.totalSessions}회
                  </span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-right">
                  잔여 {a.totalSessions - a.usedSessions}회
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card p-6 text-center">
          <Dumbbell className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">진행중인 PT가 없습니다.</p>
        </div>
      )}

      {/* 최근 PT 일지 */}
      {logs.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">최근 PT 일지</p>
          <div className="space-y-2">
            {logs.slice(0, 5).map(log => (
              <div key={log.id} className="card p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-400">
                    {log.date ? format(parseISO(log.date), 'M월 d일 (EEE)', { locale: ko }) : ''}
                  </span>
                  {log.sessionNumber && (
                    <span className="text-xs text-primary-500">{log.sessionNumber}회차</span>
                  )}
                </div>
                {log.content && <p className="text-sm text-gray-700 dark:text-gray-300">{log.content}</p>}
                {log.memo && (
                  <div className="mt-2 flex items-start gap-2 bg-primary-50 dark:bg-primary-900/20 rounded-xl px-3 py-2">
                    <MessageCircle className="w-3.5 h-3.5 text-primary-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-primary-700 dark:text-primary-300">{log.memo}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 완료된 PT 세션 */}
      {completedSessions.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">완료된 수업</p>
          <div className="space-y-2">
            {completedSessions.slice(0, 5).map(s => (
              <div key={s.id} className="card p-4 flex items-center justify-between opacity-75">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{s.productName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">트레이너: {s.trainerName}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {s.scheduledAt ? format(parseISO(s.scheduledAt), 'M월 d일 (EEE)', { locale: ko }) : '-'}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 font-medium mt-0.5">완료</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 완료된 PT 배정 */}
      {completed.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">완료</p>
          {completed.map(a => (
            <div key={a.id} className="card p-4 flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">{a.productName}</p>
              <span className="text-xs text-gray-400">{a.totalSessions}회 완료</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
